import { isBlockingAutoModeOutcome, toAutoModeOutcome } from '../../flora-tool-input-streaming';
import type { ArtifactSpec, ContentBlock, Message, MessageSegment, SubagentInfo, ToolCall } from '../../flora.type';
import type { GroupedContentBlock, GroupedSegment } from './flora-messgae.type';

/**
 * Converts a persisted `tool_call` content block into the live {@link ToolCall}
 * shape used by the tool-call UI. Chat history has no live status, so the result
 * is `completed` — except for a call that was blocked (`auto_mode` is
 * `blocked`, `unavailable` or `invalid`), which never ran and is `drafted`. The auto-mode
 * outcome and provider tool-call id are carried over when present. The `key` is
 * folded into the id to keep it unique across a rendered turn. Shared by both
 * the side panel and flora-agent message renderers so the id/status conventions
 * cannot drift.
 *
 * @param {ContentBlock & { type: 'tool_call' }} block - The persisted tool-call block
 * @param {string} key - A per-render key segment, embedded in the id for uniqueness.
 *   Callers must make this unique within the turn (e.g. include the subagent
 *   group index for nested calls) so ids don't collide across groups.
 * @returns {ToolCall} The equivalent settled tool call
 *
 * @example
 * contentBlockToToolCall(
 *   { type: 'tool_call', name: 'search_processes', input: { q: 'x' }, output: '[]' },
 *   'blk-tool-3',
 * );
 * // → { id: 'search_processes-blk-tool-3', tool_name: 'search_processes',
 * //     tool_input: { q: 'x' }, tool_output: '[]', status: 'completed' }
 */
const contentBlockToToolCall = (block: ContentBlock & { type: 'tool_call' }, key: string): ToolCall => {
  const autoMode = toAutoModeOutcome(block.auto_mode);
  return {
    id: `${block.name}-${key}`,
    tool_name: block.name,
    tool_input: block.input,
    tool_output: block.output,
    status: isBlockingAutoModeOutcome(autoMode) ? 'drafted' : 'completed',
    ...(autoMode ? { auto_mode: autoMode } : {}),
    ...(block.tool_call_id ? { tool_call_id: block.tool_call_id } : {}),
    ...(block.error ? { tool_error: block.error } : {}),
  };
};

/**
 * Type guard to check if content is a ContentBlock array (new format).
 * @param {string | ContentBlock[]} content - The message content to check
 * @returns {boolean} True if content is an array of ContentBlocks
 */
const isContentBlockArray = (content: string | ContentBlock[]): content is ContentBlock[] => {
  return Array.isArray(content);
};

/**
 * Groups persisted content blocks by subagent for rendering.
 *
 * A `task` tool_call with `is_subagent` opens a subagent group; subsequent
 * blocks join it when their `parent_context` matches the group's key. The key is
 * `subagent_id ?? subagent_name`, so older persisted chats (one sequential
 * subagent, keyed on its name) and the LangChain agent (up to three parallel
 * `general-purpose` instances, keyed on a per-instance id) both nest correctly —
 * including when parallel instances' blocks interleave in the persisted list.
 *
 * @param {ContentBlock[]} blocks - Array of content blocks to group
 * @returns {GroupedContentBlock[]} Array of grouped content blocks
 */
const groupContentBlocksBySubagent = (blocks: ContentBlock[]): GroupedContentBlock[] => {
  const groups: GroupedContentBlock[] = [];
  // Open groups keyed by subagent id/name so interleaved parallel children
  // route to the right group. Groups stay renderable in start order via `groups`.
  const openByKey = new Map<string, GroupedContentBlock & { type: 'subagent_group' }>();

  for (const block of blocks) {
    // A subagent tool_call marks the start of a subagent group.
    if (block.type === 'tool_call' && block.is_subagent && block.subagent_name) {
      const key = block.subagent_id ?? block.subagent_name;
      const group: GroupedContentBlock & { type: 'subagent_group' } = {
        type: 'subagent_group',
        subagent: {
          id: block.subagent_id,
          name: block.subagent_name,
          description: typeof block.input?.description === 'string' ? block.input.description : undefined,
          status: 'completed', // Chat history items are always completed
          // The task tool_call's output IS the subagent's final report.
          output: block.output,
        },
        blocks: [],
      };
      groups.push(group);
      openByKey.set(key, group);
      // The task tool_call is only the wrapper — don't add it to the blocks.
      continue;
    }

    const parentContext = block.type === 'text' || block.type === 'tool_call' ? block.parent_context : null;
    const group = parentContext != null ? openByKey.get(parentContext) : undefined;
    if (group) {
      group.blocks.push(block);
    } else {
      // No matching subagent (top-level, or an orphaned parent_context).
      groups.push({ type: 'standalone', block });
    }
  }

  return groups;
};

/**
 * Extracts artifact specs from a message's content blocks.
 * For content block arrays, filters for artifact-type blocks and returns their specs.
 * @param {string | ContentBlock[]} content - The message content to extract artifacts from
 * @returns {ArtifactSpec[]} Array of artifact specs found in the content
 */
const extractArtifactsFromContent = (content: string | ContentBlock[]): ArtifactSpec[] => {
  if (!isContentBlockArray(content)) return [];
  return content
    .filter((block): block is ContentBlock & { type: 'artifact' } => block.type === 'artifact')
    .map((block) => block.artifact);
};

/**
 * Extracts plain text from a message's content for clipboard operations.
 * For content block arrays, only text blocks are included.
 * @param {Message} message - The message to extract text from
 * @returns {string} Plain text string representation of the message content
 */
const getPlainTextContent = (message: Message): string => {
  if (typeof message.content === 'string') {
    return message.content;
  }
  // For content block array, extract text blocks only
  return message.content
    .filter((block): block is ContentBlock & { type: 'text' } => block.type === 'text')
    .map((block) => block.text)
    .join('\n');
};

/**
 * Checks if a message has displayable content (text, segments, or content blocks).
 * @param {Message} message - The message to check
 * @returns {boolean} True if the message has content that can be displayed
 */
const hasDisplayableContent = (message: Message): boolean => {
  if (!message) return false;

  // Check segments (streaming/live messages)
  if (message.segments && message.segments.length > 0) return true;

  // Check content
  if (typeof message.content === 'string') {
    return message.content.length > 0;
  }

  // Content block array
  return Array.isArray(message.content) && message.content.length > 0;
};

/**
 * Returns the subagent a live segment belongs to, or null for a top-level one.
 *
 * Text and tool-call segments carry a `parent_context`; the backend sets it to
 * the owning subagent's key (its id, or its name for older persisted chats).
 *
 * @param {MessageSegment} segment - The segment to inspect
 * @returns {string | null} The parent subagent key, or null
 */
function segmentParentContext(segment: MessageSegment): string | null {
  if (segment.type === 'text') return segment.parent_context ?? null;
  if (segment.type === 'tool_call') return segment.toolCall.parent_context ?? null;
  return null;
}

/**
 * Groups live message segments by subagent for rendering.
 *
 * A `subagent_start` opens a group keyed by `subagent_id ?? subagent_name`;
 * later segments join it when their `parent_context` matches that key, and a
 * `subagent_end` closes it (merging final status/output/error). Keying — rather
 * than tracking a single "current" subagent — lets the LangChain agent's up-to-
 * three parallel `general-purpose` instances render as distinct sections even
 * when their streamed events interleave, while a single sequential subagent
 * without an id remains the one-group special case.
 *
 * @param {MessageSegment[]} segments - Array of message segments to group
 * @returns {GroupedSegment[]} Array of grouped message segments
 */
function groupSegmentsBySubagent(segments: MessageSegment[]): GroupedSegment[] {
  const groups: GroupedSegment[] = [];
  // Open groups keyed by subagent id/name; groups render in start order.
  const openByKey = new Map<string, GroupedSegment & { type: 'subagent_group' }>();
  const keyOf = (subagent: SubagentInfo): string => subagent.id ?? subagent.name;
  // Each subagent_start is immediately followed by its own `task` tool call (the
  // wrapper). We drop that wrapper here so the section — not a stray tool chip —
  // represents it. A `task` call with NO preceding start (e.g. a reconnect's
  // flat replay, where lifecycle events were not re-emitted) is left standalone
  // so the caller can render it as a normal tool call and keep its output.
  let pendingWrapperTasks = 0;

  for (const segment of segments) {
    if (segment.type === 'subagent_start') {
      const group: GroupedSegment & { type: 'subagent_group' } = {
        type: 'subagent_group',
        subagent: segment.subagent,
        segments: [],
      };
      groups.push(group);
      openByKey.set(keyOf(segment.subagent), group);
      pendingWrapperTasks += 1;
      continue;
    }

    if (segment.type === 'subagent_end') {
      const group = openByKey.get(keyOf(segment.subagent));
      if (group) {
        // Keep the start's description; overlay the terminal status/output/error.
        group.subagent = {
          ...group.subagent,
          status: segment.subagent.status,
          output: segment.subagent.output,
          error: segment.subagent.error,
        };
        openByKey.delete(keyOf(segment.subagent));
      }
      continue;
    }

    const parentContext = segmentParentContext(segment);
    const group = parentContext != null ? openByKey.get(parentContext) : undefined;
    if (group) {
      group.segments.push(segment);
    } else if (segment.type === 'tool_call' && segment.toolCall.tool_name === 'task' && pendingWrapperTasks > 0) {
      // The wrapper for a group we just opened — drop it, don't render a chip.
      pendingWrapperTasks -= 1;
    } else {
      groups.push({ type: 'standalone', segment });
    }
  }

  // Any group still open (subagent never ended) stays renderable in progress.
  return groups;
}

/**
 * Reports whether an answer message renders clarifying-question card(s), so the
 * message bubble can pin itself to full width and keep every question card a
 * consistent size instead of shrink-wrapping to each question's content.
 *
 * Mirrors the renderer's branch precedence — live segments first, then persisted
 * content blocks — so detection cannot drift from what actually renders a card.
 * Shared by both the flora side panel and the flora-agent message renderers.
 *
 * @param {Message | undefined} message - The answer message to inspect
 * @returns {boolean} True when the message renders at least one question card
 */
const messageHasQuestionCard = (message: Message | undefined): boolean => {
  if (!message) return false;
  if (message.segments && message.segments.length > 0) {
    return message.segments.some((segment) => segment.type === 'ask_user_questions');
  }
  if (isContentBlockArray(message.content)) {
    return message.content.some((block) => block.type === 'ask_user_questions');
  }
  return false;
};

/**
 * The prompts in a live message that may still take user input. At most one
 * card of each kind is actionable, and only the message's trailing prompt:
 * any prompt followed by a later one has already been resolved (a replay can
 * re-send a decided approval with no decision, and a sequential re-ask leaves
 * the earlier, answered card in place).
 */
interface ActionablePrompts {
  /** The `ask_user_questions` segment whose card can be submitted, if any. */
  question?: MessageSegment & { type: 'ask_user_questions' };
  /** The `tool_approval` segment whose card can be decided, if any. */
  approval?: MessageSegment & { type: 'tool_approval' };
}

/** Segment types that mean the run moved on past a tool approval. */
const CONTENT_AFTER_APPROVAL_TYPES: ReadonlySet<MessageSegment['type']> = new Set([
  'text',
  'tool_call',
  'artifact',
  'subagent_start',
  'user_answers',
]);

/**
 * Finds the prompt in a live message that is still waiting on the user, so the
 * bubble arms exactly one card. The message-level `pendingQuestions` /
 * `pendingJobId` / `pendingToolApproval` slots say *that* something is pending;
 * this decides *which* card it is: the last `ask_user_questions` or
 * `tool_approval` segment, and only when it is unresolved — a question with no
 * `user_answers` after it, an approval with no decision and no later content.
 *
 * @param {Message | undefined} message - The assistant message to inspect
 * @returns {ActionablePrompts} The actionable question and/or approval segment (at most one is set)
 *
 * @example
 * // [ask(Q1), user_answers, text, ask(Q2)] with pendingQuestions = Q2
 * getActionablePrompts(message).question; // → the ask(Q2) segment, never ask(Q1)
 */
const getActionablePrompts = (message: Message | undefined): ActionablePrompts => {
  const segments = message?.segments;
  if (!message || !segments || segments.length === 0) return {};
  let promptIndex = -1;
  for (let i = segments.length - 1; i >= 0; i--) {
    const type = segments[i].type;
    if (type === 'ask_user_questions' || type === 'tool_approval') {
      promptIndex = i;
      break;
    }
  }
  if (promptIndex === -1) return {};
  const prompt = segments[promptIndex];
  const after = segments.slice(promptIndex + 1);
  if (prompt.type === 'ask_user_questions') {
    const hasPendingQuestions = Boolean(message.pendingQuestions?.length) && Boolean(message.pendingJobId);
    const isAnswered = after.some((segment) => segment.type === 'user_answers');
    return hasPendingQuestions && !isAnswered ? { question: prompt } : {};
  }
  if (prompt.type === 'tool_approval') {
    const hasMovedOn = after.some((segment) => CONTENT_AFTER_APPROVAL_TYPES.has(segment.type));
    return message.pendingToolApproval && !prompt.decision && !hasMovedOn ? { approval: prompt } : {};
  }
  return {};
};

/**
 * Finds the answers submitted for a question card: the first `user_answers`
 * segment after it, stopping at the next prompt so a later card's answers are
 * never shown on an earlier one.
 *
 * @param {MessageSegment[]} segments - The message's live segments
 * @param {MessageSegment} questionSegment - The `ask_user_questions` segment to pair
 * @returns {Record<string, string | string[]> | undefined} The paired answers, or undefined when unanswered
 */
const findSubmittedAnswers = (
  segments: MessageSegment[],
  questionSegment: MessageSegment,
): Record<string, string | string[]> | undefined => {
  const start = segments.indexOf(questionSegment);
  if (start === -1) return undefined;
  for (const segment of segments.slice(start + 1)) {
    if (segment.type === 'user_answers') return segment.answers;
    if (segment.type === 'ask_user_questions' || segment.type === 'tool_approval') return undefined;
  }
  return undefined;
};

export {
  contentBlockToToolCall,
  extractArtifactsFromContent,
  findSubmittedAnswers,
  getActionablePrompts,
  getPlainTextContent,
  groupContentBlocksBySubagent,
  groupSegmentsBySubagent,
  hasDisplayableContent,
  isContentBlockArray,
  messageHasQuestionCard,
};
export type { ActionablePrompts };
